import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import SEO from '../components/SEO';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      details: ['0404 844 257', 'Mon-Fri: 8am - 6pm'],
      link: 'tel:0404844257'
    },
    {
      icon: Mail,
      title: 'Email',
      details: ['info@energyspectrum.com.au', 'We reply within 24 hours'],
      link: 'mailto:info@energyspectrum.com.au'
    },
    {
      icon: MapPin,
      title: 'Location',
      details: ['Leppington, NSW 2179', 'Serving all of New South Wales'],
      link: null
    },
    {
      icon: Clock,
      title: 'Business Hours',
      details: ['Monday - Friday: 8am - 6pm', 'Saturday: 9am - 4pm'],
      link: null
    }
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission (mock)
    setTimeout(() => {
      toast({
        title: "Message Sent!",
        description: "Thank you for contacting Energy Spectrum. We'll get back to you within 24 hours.",
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        message: ''
      });
      setIsSubmitting(false);
    }, 1000);
  };

  return (
    <>
      <SEO 
        title="Contact Energy Spectrum | Free Solar Quote Sydney | Call 0404 844 257"
        description="Contact Energy Spectrum for free solar consultation in Sydney. Call 0404 844 257 or email info@energyspectrum.com.au. Leppington NSW. Professional solar and battery installation."
        keywords="solar quote Sydney, contact solar installer Sydney, solar consultation NSW, Energy Spectrum contact, solar installer Leppington"
      />
      <div className="contact-container">
        {/* Hero Section */}
        <section className="page-hero">
        <div className="container">
          <h1 className="display-medium">Contact Us</h1>
          <p className="body-large page-hero-description">
            Get in touch with our team for a free consultation and quote
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="contact-content-section">
        <div className="container">
          <div className="contact-layout">
            {/* Contact Form */}
            <div className="contact-form-container">
              <Card className="contact-form-card">
                <h2 className="heading-2">Send Us a Message</h2>
                <p className="body-medium form-intro">
                  Fill out the form below and our team will get back to you shortly
                </p>
                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="form-group">
                    <label htmlFor="name" className="form-label">Full Name *</label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Smith"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email" className="form-label">Email Address *</label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@example.com"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone" className="form-label">Phone Number</label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="0400 000 000"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="address" className="form-label">Address *</label>
                    <Input
                      id="address"
                      name="address"
                      type="text"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      placeholder="123 Street Name, Suburb, NSW 2000"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="message" className="form-label">Message *</label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      placeholder="Tell us about your solar energy needs..."
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="btn-primary submit-button"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </Card>
            </div>

            {/* Contact Information */}
            <div className="contact-info-container">
              <h2 className="heading-2">Get In Touch</h2>
              <p className="body-medium contact-intro">
                Have questions? Our team is here to help you make the switch to solar energy.
              </p>
              <div className="contact-info-grid">
                {contactInfo.map((info, index) => {
                  const IconComponent = info.icon;
                  const content = (
                    <Card key={index} className="contact-info-card">
                      <div className="contact-info-icon">
                        <IconComponent size={28} />
                      </div>
                      <h3 className="heading-3">{info.title}</h3>
                      {info.details.map((detail, dIndex) => (
                        <p key={dIndex} className="body-small contact-detail">
                          {detail}
                        </p>
                      ))}
                    </Card>
                  );

                  return info.link ? (
                    <a key={index} href={info.link} className="contact-info-link">
                      {content}
                    </a>
                  ) : content;
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map/Location Section */}
      <section className="location-section">
        <div className="container">
          <Card className="location-card">
            <div className="location-content">
              <h2 className="heading-2">Serving All of NSW</h2>
              <p className="body-medium">
                Based in Leppington, Sydney, Energy Spectrum proudly serves residential and commercial 
                customers throughout New South Wales. We're expanding our reach to bring 
                clean energy solutions to more Australians every day.
              </p>
              <div className="service-areas">
                <div className="service-area">
                  <h4 className="body-medium" style={{ fontWeight: 600 }}>Current Service Areas:</h4>
                  <p className="body-small">Greater Sydney, Central Coast, Newcastle, Wollongong, Blue Mountains</p>
                </div>
                <div className="service-area">
                  <h4 className="body-medium" style={{ fontWeight: 600 }}>Coming Soon:</h4>
                  <p className="body-small">Regional NSW and interstate expansion</p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>
      </div>
    </>
  );
};

export default Contact;